import React, { useState, useEffect, useRef } from 'react';
import { X, Package, AlertCircle, CheckCircle, Loader2, MessageSquare } from 'lucide-react';
import { useOrderCancel } from '../context/OrderCancelContext';

const OrderCancelModal = ({
  isOpen,
  onClose,
  order,
  onCancelSubmitted = null
}) => {
  const modalRef = useRef(null);

  const {
    loading,
    error,
    submitCancelRequest,
    clearError
  } = useOrderCancel();

  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [validationError, setValidationError] = useState('');

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen && !isSubmitting) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
      setTimeout(() => {
        if (modalRef.current) {
          modalRef.current.focus();
        }
      }, 100);
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose, isSubmitting]);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setReason('');
      setIsSubmitting(false);
      setSubmitSuccess(false);
      setValidationError('');
      clearError();
    }
  }, [isOpen, clearError]);

  const validateForm = () => {
    if (!reason.trim()) {
      setValidationError('Please provide a reason for cancellation');
      return false;
    }

    if (reason.trim().length < 10) {
      setValidationError('Reason must be at least 10 characters');
      return false;
    }

    setValidationError('');
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const cancelData = {
        orderId: order.id,
        reason: reason.trim()
      };

      console.log('Submitting cancel request:', cancelData);

      const result = await submitCancelRequest(cancelData);

      if (result.success) {
        console.log('Cancel request submitted successfully:', result);
        setSubmitSuccess(true);

        // Show success message briefly before closing
        setTimeout(() => {
          // Call the success callback if provided
          if (onCancelSubmitted) {
            onCancelSubmitted(order.id);
          }

          // Close the modal
          onClose();
        }, 2000);
      } else {
        console.error('Cancel request submission failed:', result.error);
        // Error is already set in the context, so it will be displayed
      }
    } catch (error) {
      console.error('Error submitting cancel request:', error);
      // Error handling is done in the context
    } finally {
      // Keep isSubmitting true until modal closes to prevent multiple submissions
      // Don't set it to false here as we want to keep the button disabled
    }
  };

  const handleCancel = () => {
    if (!isSubmitting) {
      onClose();
      clearError();
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !isSubmitting) {
      onClose();
    }
  };

  if (!isOpen || !order) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-40 transition-opacity duration-300"
        onClick={handleBackdropClick}
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        ref={modalRef}
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cancel-modal-title"
        tabIndex={-1}
      >
        <div className="w-full max-w-2xl max-h-[90vh] overflow-hidden bg-white shadow-2xl rounded-2xl transform transition-all duration-300 scale-100">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-red-50 to-orange-50">
            <div>
              <h2 id="cancel-modal-title" className="text-2xl font-bold text-gray-900">
                Cancel Order
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                Order {order.orderNumber} • {order.items?.length || 0} item{order.items?.length !== 1 ? 's' : ''}
              </p>
              {submitSuccess && (
                <div className="mt-2 flex items-center gap-2 text-sm text-green-700 bg-green-100 px-3 py-1 rounded-full">
                  <CheckCircle className="w-4 h-4" />
                  Cancellation request submitted successfully!
                </div>
              )}
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={onClose}
                disabled={isSubmitting}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Close modal"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="overflow-y-auto max-h-[calc(90vh-120px)]">
            <div className="p-6">
              {/* Order Items Preview */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Package className="w-5 h-5 text-red-600" />
                  Order Items
                </h3>
                <div className="flex flex-wrap gap-3">
                  {order.items?.slice(0, 4).map((item, index) => (
                    <div key={item.id} className="relative">
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-16 h-16 object-cover rounded-lg border border-gray-200"
                        loading="lazy"
                      />
                      {item.quantity > 1 && (
                        <div className="absolute -top-2 -right-2 w-6 h-6 bg-red-600 text-white text-xs font-bold rounded-full flex items-center justify-center">
                          {item.quantity}
                        </div>
                      )}
                    </div>
                  ))}
                  {order.items?.length > 4 && (
                    <div className="w-16 h-16 bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center">
                      <span className="text-sm font-medium text-gray-600">
                        +{order.items.length - 4}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Warning Message */}
              <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-medium text-yellow-800 mb-1">Important Notice</h4>
                    <p className="text-sm text-yellow-700">
                      Once you submit this cancellation request, it will be reviewed by our team.
                      Please note that cancellation may not be possible if your order has already been processed or shipped.
                    </p>
                  </div>
                </div>
              </div>

              {/* Cancellation Form */}
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Reason */}
                <div>
                  <label htmlFor="cancel-reason" className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                    <MessageSquare className="w-4 h-4" />
                    Reason for Cancellation
                  </label>
                  <textarea
                    id="cancel-reason"
                    value={reason}
                    onChange={(e) => {
                      setReason(e.target.value);
                      if (validationError) {
                        setValidationError('');
                      }
                    }}
                    placeholder="Please provide a detailed reason for cancelling this order..."
                    rows={4}
                    disabled={isSubmitting}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-400 focus:border-red-400 resize-none ${
                      validationError ? 'border-red-300' : 'border-gray-300'
                    } ${isSubmitting ? 'opacity-50 cursor-not-allowed bg-gray-50' : ''}`}
                    required
                    minLength={10}
                  />
                  <div className="flex justify-between items-center mt-1">
                    {validationError ? (
                      <p className="text-sm text-red-600">{validationError}</p>
                    ) : (
                      <p className="text-sm text-gray-500">
                        Minimum 10 characters ({reason.length}/10)
                      </p>
                    )}
                  </div>
                </div>

                {/* Error Message */}
                {error && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-center gap-2 text-red-800 text-sm font-medium mb-1">
                      <AlertCircle className="w-4 h-4" />
                      Error
                    </div>
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                )}

                {/* Success Message */}
                {submitSuccess && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center gap-2 text-green-800 text-sm font-medium mb-1">
                      <CheckCircle className="w-4 h-4" />
                      Request Submitted Successfully!
                    </div>
                    <p className="text-sm text-green-700">
                      Your cancellation request has been submitted. We'll review it and get back to you soon.
                    </p>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-3 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={handleCancel}
                    disabled={isSubmitting}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Keep Order
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting || submitSuccess || !reason.trim() || reason.trim().length < 10}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Submitting...
                      </>
                    ) : submitSuccess ? (
                      <>
                        <CheckCircle className="w-4 h-4" />
                        Submitted!
                      </>
                    ) : (
                      <>
                        <X className="w-4 h-4" />
                        Cancel Order
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default OrderCancelModal;
